import * as React from 'react';
import { Link } from 'react-router-dom';

interface Props {
  children: React.ReactNode;
  labelledBy?: string;
  title?: string;
  closeTo?: string;
  closeLabel?: string;
  onRequestClose?: () => void;
}

interface OverlayState {
  previouslyFocused: HTMLElement | null;
}

export default class Overlay extends React.Component<Props, OverlayState> {
  public state: OverlayState = { previouslyFocused: null };
  private dialogRef: HTMLDivElement | null = null;

  public componentDidMount() {
    this.setState({ previouslyFocused: document.activeElement as HTMLElement });
    document.addEventListener('keydown', this.onKeyDown);
    window.setTimeout(() => {
      if (!this.dialogRef) {
        return;
      }
      const focusable = this.dialogRef.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable) {
        focusable.focus();
      } else {
        this.dialogRef.focus();
      }
    }, 0);
  }

  public componentWillUnmount() {
    document.removeEventListener('keydown', this.onKeyDown);
    const { previouslyFocused } = this.state;
    if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
      previouslyFocused.focus();
    }
  }

  private onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      this.requestClose();
      return;
    }
    if (event.key !== 'Tab' || !this.dialogRef) {
      return;
    }
    const focusable = Array.from(
      this.dialogRef.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => !el.hasAttribute('disabled') && el.offsetParent !== null);
    if (focusable.length === 0) {
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  private requestClose = () => {
    const { onRequestClose, closeTo } = this.props;
    if (onRequestClose) {
      onRequestClose();
      return;
    }
    if (closeTo && window.history) {
      // Prefer explicit close route via Link click simulation when provided.
      const link = this.dialogRef && this.dialogRef.querySelector<HTMLAnchorElement>('.cc__overlay__close');
      if (link) {
        link.click();
      }
    }
  }

  public render() {
    const { children, labelledBy, title, closeTo, closeLabel } = this.props;
    return (
      <div className="cc__overlay__container">
        <div className="cc__overlay" aria-hidden={true} />
        <div className="cc__overlay__content">
          <div
            className="cc__overlay__content__inner"
            role="dialog"
            aria-modal="true"
            aria-labelledby={labelledBy || 'cc__auth_dialog_title'}
            tabIndex={-1}
            ref={(el) => { this.dialogRef = el; }}
          >
            {closeTo && (
              <div className="cc__overlay__close_row">
                <Link className="cc__overlay__close" to={closeTo}>
                  {closeLabel || 'Close'}
                </Link>
              </div>
            )}
            {children}
          </div>
        </div>
      </div>
    );
  }
}
